import pptxgen from 'pptxgenjs';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { getToken } from '../../../api/client';
import { resolveArcaAssetUrl } from './arcaApi';
import { SLIDE_LAYOUTS } from './slideSchema';
import { resolveSlideTheme } from './slideThemes';

function safeFileName(name) {
  return (
    String(name || 'apresentacao')
      .replace(/[<>:"/\\|?*]/g, '')
      .trim()
      .slice(0, 80) || 'apresentacao'
  );
}

function hexToPptxColor(hex) {
  return String(hex || '#0F172A')
    .replace('#', '')
    .toUpperCase()
    .slice(0, 6);
}

async function mediaToDataUrl(src) {
  if (!src) return null;
  if (String(src).startsWith('data:')) return src;

  const url = resolveArcaAssetUrl(src);
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, { headers });
  if (!res.ok) return null;
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const CHART_TYPE_MAP = (pptx) => ({
  bar: pptx.charts.BAR,
  line: pptx.charts.LINE,
  pie: pptx.charts.PIE,
  doughnut: pptx.charts.DOUGHNUT,
});

async function appendSlideToPptx(pptx, slide) {
  const theme = resolveSlideTheme(slide?.theme);
  const tip = slide?.tipografia || {};
  const s = pptx.addSlide();
  s.background = { color: hexToPptxColor(theme.bg) };

  const textColor = hexToPptxColor(theme.text);
  const mutedColor = hexToPptxColor(theme.muted);
  const accentColor = hexToPptxColor(theme.accent);

  const isCenter = slide.layout === SLIDE_LAYOUTS.TITULO_CENTRO;
  const isTwoCol = slide.layout === SLIDE_LAYOUTS.DUAS_COLUNAS;
  const chartMap = CHART_TYPE_MAP(pptx);
  const graficos = slide.graficos || [];
  const tituloSize = Number(tip.tituloSize) || 32;
  const subtituloSize = Number(tip.subtituloSize) || 16;
  const bulletSize = Number(tip.bulletSize) || 15;
  const boldTitulo = tip.boldTitulo !== false;
  const align = tip.align === 'center' || isCenter ? 'center' : tip.align === 'right' ? 'right' : 'left';

  if (isCenter) {
    s.addText(slide.titulo || '', {
      x: 0.5,
      y: 2.2,
      w: 12.3,
      h: 1.2,
      fontSize: tituloSize,
      bold: boldTitulo,
      color: textColor,
      align: 'center',
    });
    if (slide.subtitulo) {
      s.addText(slide.subtitulo, {
        x: 0.5,
        y: 3.5,
        w: 12.3,
        h: 0.8,
        fontSize: subtituloSize,
        color: mutedColor,
        align: 'center',
      });
    }
    return;
  }

  s.addText(slide.titulo || '', {
    x: 0.5,
    y: 0.35,
    w: 12,
    h: 0.85,
    fontSize: tituloSize,
    bold: boldTitulo,
    color: textColor,
    align,
  });
  if (slide.subtitulo) {
    s.addText(slide.subtitulo, {
      x: 0.5,
      y: 1.15,
      w: 12,
      h: 0.55,
      fontSize: subtituloSize,
      color: mutedColor,
      align,
    });
  }

  let yStart = 1.85;
  const midias = slide.midias || [];
  if (midias.length) {
    for (const m of midias) {
      const data = await mediaToDataUrl(m.src || m.url);
      if (!data) continue;
      const wPct = Number(m.w ?? m.largura) || 32;
      const xPct = m.x != null ? Number(m.x) : 8;
      const yPct = m.y != null ? Number(m.y) : 28;
      // Slide 16:9 pptxgen default ~13.33 x 7.5 in
      const slideW = 13.33;
      const slideH = 7.5;
      const w = Math.max(0.8, (wPct / 100) * slideW);
      const x = (xPct / 100) * slideW;
      const y = (yPct / 100) * slideH;
      s.addImage({ data, x, y, w, h: w * 0.62 });
    }
    yStart = Math.max(yStart, 4.2);
  }

  if (graficos.length) {
    const chartW = graficos.length > 1 ? 5.8 : 11.5;
    const chartH = 3.2;
    for (let i = 0; i < graficos.length; i++) {
      const g = graficos[i];
      const col = i % 2;
      const row = Math.floor(i / 2);
      s.addChart(
        chartMap[g.tipo] || pptx.charts.BAR,
        [
          {
            name: g.titulo || 'Série',
            labels: g.labels,
            values: g.valores,
          },
        ],
        {
          x: 0.5 + col * (chartW + 0.4),
          y: yStart + row * (chartH + 0.25),
          w: chartW,
          h: chartH,
          showTitle: true,
          title: g.titulo || '',
          chartColors: [accentColor, '34D399', 'FBBF24', 'A78BFA', 'F87171'],
        }
      );
    }
    const chartRows = Math.ceil(graficos.length / 2);
    yStart += chartRows * (chartH + 0.35);
  }

  const bullets = (slide.bullets || []).map((b) => String(b).trim()).filter(Boolean);
  const addBulletColumn = (items, x, w) => {
    let y = yStart;
    items.forEach((b) => {
      if (y > 6.8) return;
      s.addText(b, {
        x,
        y,
        w,
        h: 0.45,
        fontSize: bulletSize,
        color: mutedColor,
        bullet: true,
      });
      y += 0.48;
    });
  };

  if (bullets.length) {
    if (isTwoCol && bullets.length > 1) {
      const mid = Math.ceil(bullets.length / 2);
      addBulletColumn(bullets.slice(0, mid), 0.5, 5.8);
      addBulletColumn(bullets.slice(mid), 6.8, 5.8);
    } else {
      addBulletColumn(bullets, 0.5, 12);
    }
  }
}

/**
 * Exporta todos os slides da apresentação para .pptx.
 * @param {object[]} slides
 * @param {string} fileName
 */
export async function exportApresentacaoPptx(slides, fileName) {
  if (!slides?.length) throw new Error('Nenhum slide para exportar.');

  const pptx = new pptxgen();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'SGC ARCA';
  pptx.title = fileName || slides[0]?.titulo || 'Apresentação';

  for (const slide of slides) {
    await appendSlideToPptx(pptx, slide);
  }

  await pptx.writeFile({ fileName: `${safeFileName(fileName)}.pptx` });
}

/** @deprecated use exportApresentacaoPptx */
export async function exportSlidePptx(slide, fileName) {
  return exportApresentacaoPptx([slide], fileName);
}

async function capturePreviewPage(previewElement) {
  return html2canvas(previewElement, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#0f172a',
    ignoreElements: (el) => el.classList?.contains('arca-export-hide'),
    logging: false,
  });
}

function addCanvasToPdf(pdf, canvas, isFirstPage) {
  const imgData = canvas.toDataURL('image/png');
  if (!isFirstPage) pdf.addPage();
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const maxW = pageW - margin * 2;
  const maxH = pageH - margin * 2;
  const pxToMm = 0.264583;
  const imgWmm = canvas.width * pxToMm;
  const imgHmm = canvas.height * pxToMm;
  const ratio = Math.min(maxW / imgWmm, maxH / imgHmm);
  const w = imgWmm * ratio;
  const h = imgHmm * ratio;
  pdf.addImage(imgData, 'PNG', (pageW - w) / 2, (pageH - h) / 2, w, h);
}

/**
 * Exporta todos os slides como PDF (captura WYSIWYG de cada slide).
 * @param {object[]} slides
 * @param {HTMLElement} previewElement
 * @param {string} fileName
 * @param {(slideId: string) => void} [onBeforeCapture]
 */
export async function exportApresentacaoPdf(slides, previewElement, fileName, onBeforeCapture) {
  if (!slides?.length) throw new Error('Nenhum slide para exportar.');
  if (!previewElement) throw new Error('Preview do slide não encontrado.');

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  for (let i = 0; i < slides.length; i++) {
    if (onBeforeCapture) {
      onBeforeCapture(slides[i].id);
      await new Promise((r) => setTimeout(r, 500));
    }
    const canvas = await capturePreviewPage(previewElement);
    addCanvasToPdf(pdf, canvas, i === 0);
  }

  pdf.save(`${safeFileName(fileName)}.pdf`);
}

/** @deprecated use exportApresentacaoPdf */
export async function exportSlidePdf(previewElement, fileName) {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const canvas = await capturePreviewPage(previewElement);
  addCanvasToPdf(pdf, canvas, true);
  pdf.save(`${safeFileName(fileName)}.pdf`);
}
